import { MapModuleLayer } from "@iventis/map-engine";
import { MapObjectProperties } from "@iventis/map-types";
import GeoJSON from "geojson";
import { useSelector } from "react-redux";
import { Content } from "@iventis/translations/content/typed-content";
import { LoadingComponent } from "@iventis/components";
import React, { FunctionComponent, useRef } from "react";
import { styled } from "@iventis/styles";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { searchMapObjectsByArea } from "@iventis/plans/src/services/map-objects";
import { css } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { contentCss } from "./analysis.helpers";

export const AnalysisCountComponent: FunctionComponent<{ layers: MapModuleLayer[]; feature: GeoJSON.Feature<GeoJSON.Polygon, MapObjectProperties>; mapId: string }> = ({
    layers,
    feature,
    mapId,
}) => {
    const translate = useIventisTranslate();
    const abortControllerRef = useRef<AbortController | null>(null);

    const { currentLevel, datesFilter } = useSelector((s: { mapReducer: MapGlobalState }) => s.mapReducer.mapModule);

    const { data: objectGroupsList, isLoading } = useQuery(
        ["countMapObjects", JSON.stringify(feature.geometry.coordinates)],
        () => fetchMapObjectGroups(feature.geometry.coordinates as number[][][]),
        {
            enabled: feature.geometry.coordinates[0].length >= 4,
        }
    );

    const fetchMapObjectGroups = async (coordinates: number[][][]) => {
        // At least 4 points in the polygon geometry
        if (coordinates[0].length >= 4) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            const data = await searchMapObjectsByArea(
                coordinates as number[][][],
                layers,
                mapId,
                currentLevel,
                datesFilter.value.filter ? { day: datesFilter.value.day, time: datesFilter.value.time } : null,
                abortControllerRef.current.signal
            );

            const groups = data.reduce((previousValue, currentValue) => {
                const newValue = previousValue;
                if (newValue[currentValue.layerId]) {
                    newValue[currentValue.layerId] += 1;
                } else {
                    newValue[currentValue.layerId] = 1;
                }
                return newValue;
            }, {} as { [id: string]: number });

            return Object.keys(groups).map((id) => ({ id, name: layers.find((l) => l.id === id)?.name, count: groups[id] }));
        }
        return [];
    };

    return (
        <StyledContent>
            <TableContainer>
                <Table data-testid="count-analysis-result">
                    <StyledTableHead>
                        <TableRow>
                            <TableCell>{translate(Content.map.labels.layer)}</TableCell>
                            <TableCell align="right">#</TableCell>
                        </TableRow>
                    </StyledTableHead>
                    <StyledTableBody>
                        {objectGroupsList &&
                            objectGroupsList.map((row) => (
                                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }} key={row.name}>
                                    <TableCell component="th" scope="row">
                                        {row.name}
                                    </TableCell>
                                    <TableCell align="right" data-testid={`count-analysis-${row.name}-value`}>
                                        {row.count}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </StyledTableBody>
                </Table>
            </TableContainer>
            {isLoading && <LoadingComponent />}
        </StyledContent>
    );
};

const StyledContent = styled.div`
    ${contentCss}
`;

const countTableStyles = css`
    .MuiTableCell-root,
    .MuiTableCell-head,
    .MuiTableCell-body {
        color: #ffffff;
    }
`;

const StyledTableHead = styled(TableHead)`
    ${countTableStyles}
`;

const StyledTableBody = styled(TableBody)`
    ${countTableStyles}
`;
